import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Plus, ArrowLeft, TrendingUp, AlertCircle, Activity } from 'lucide-react';

const STATUS_COLOR = {
  idea: '#94A3B8',
  project: '#3498DB',
  program: '#FFD93D',
  company: '#3EF1C7',
  active: '#10B981',
  paused: '#94A3B8',
};

const STATUS_LABEL = {
  idea: 'Idea',
  project: 'Project',
  program: 'Program',
  company: 'Company',
  active: 'Active',
  paused: 'Paused',
};

export default function PortfolioDashboardPage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ownerId, setOwnerId] = useState('mithun');

  useEffect(() => {
    fetch(`/api/v2/portfolio/summary?owner_id=${ownerId}`)
      .then(r => r.json())
      .then(d => { setSummary(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [ownerId]);

  const seedMithun = async () => {
    await fetch(`/api/v2/portfolio/seed/mithun?owner_id=${ownerId}`, { method: 'POST' });
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-[#22304A] bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back to Commerce Index
          </Link>
          <div className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-[#3EF1C7]" />
            <span className="font-semibold">Portfolio</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-3">Manage every business from one place.</h1>
            <p className="text-lg text-[#94A3B8] max-w-2xl">
              Idea → T-shirt sized estimate → project → company. Text the system from WhatsApp.
              Get insights via 240-480 char messages. One dashboard for all your ventures.
            </p>
          </div>
          <button onClick={seedMithun} className="px-4 py-2 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold text-sm">
            Seed Mithun's 9 businesses
          </button>
        </div>

        {loading ? (
          <div className="text-[#94A3B8]">Loading portfolio...</div>
        ) : (
          <>
            {summary && summary.business_count > 0 ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5">
                    <Briefcase className="w-5 h-5 text-[#3EF1C7] mb-2" />
                    <div className="text-3xl font-bold">{summary.business_count}</div>
                    <div className="text-xs text-[#64748B] mt-1">Businesses</div>
                  </div>
                  <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5">
                    <TrendingUp className="w-5 h-5 text-[#10B981] mb-2" />
                    <div className="text-3xl font-bold text-[#10B981]">
                      ${summary.total_monthly_revenue_usd?.toLocaleString() || 0}
                    </div>
                    <div className="text-xs text-[#64748B] mt-1">Monthly revenue</div>
                  </div>
                  <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5">
                    <AlertCircle className="w-5 h-5 text-[#FFA500] mb-2" />
                    <div className="text-3xl font-bold text-[#FFA500]">
                      ${summary.total_monthly_burn_usd?.toLocaleString() || 0}
                    </div>
                    <div className="text-xs text-[#64748B] mt-1">Monthly burn</div>
                  </div>
                  <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5">
                    <Activity className="w-5 h-5 text-[#9B59B6] mb-2" />
                    <div className={`text-3xl font-bold ${summary.net_monthly_usd >= 0 ? 'text-[#10B981]' : 'text-[#FF6B6B]'}`}>
                      ${summary.net_monthly_usd?.toLocaleString() || 0}
                    </div>
                    <div className="text-xs text-[#64748B] mt-1">Net / month</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {summary.businesses.map(biz => (
                    <Link
                      key={biz.idea_id}
                      to={`/portfolio/${biz.idea_id}`}
                      className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5 hover:bg-[#11192A] transition"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="font-semibold text-lg">{biz.name}</div>
                          {biz.domain && (
                            <div className="text-xs text-[#64748B] mt-1">{biz.domain}</div>
                          )}
                        </div>
                        <span
                          className="text-xs px-2 py-1 rounded font-mono"
                          style={{
                            background: `${STATUS_COLOR[biz.status]}20`,
                            color: STATUS_COLOR[biz.status],
                          }}
                        >
                          {STATUS_LABEL[biz.status]}
                        </span>
                      </div>
                      <div className="text-sm text-[#94A3B8] mb-3 line-clamp-2">{biz.description}</div>
                      <div className="flex justify-between text-xs text-[#64748B] mt-3 pt-3 border-t border-[#22304A]">
                        <span>Burn: ${(biz.monthly_burn_usd || 0).toLocaleString()}/mo</span>
                        <span>Revenue: ${(biz.monthly_revenue_usd || 0).toLocaleString()}/mo</span>
                      </div>
                    </Link>
                  ))}
                  <Link
                    to="/portfolio/new"
                    className="bg-[#0D1628] border border-dashed border-[#22304A] rounded-xl p-5 hover:bg-[#11192A] flex items-center justify-center text-[#94A3B8] hover:text-white"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add new business
                  </Link>
                </div>
              </>
            ) : (
              <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-12 text-center">
                <Briefcase className="w-12 h-12 text-[#3EF1C7] mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-semibold mb-2">No businesses yet</h3>
                <p className="text-sm text-[#94A3B8] mb-6 max-w-md mx-auto">
                  Text us "I have an idea for..." from WhatsApp to start your first business,
                  or click below to seed Mithun's 9 businesses as a demo.
                </p>
                <button onClick={seedMithun} className="px-4 py-2 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold">
                  Seed demo portfolio
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
